import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { StepValidationResult } from './workflow/workflowStepsApi'

type NavigationGuard = (proceed: () => void) => void

type StepValidationContextType = {
  validationResult: StepValidationResult
  setValidationResult: Dispatch<SetStateAction<StepValidationResult>>
  navigationGuard: NavigationGuard | null
  setNavigationGuard: (guard: NavigationGuard | null) => void
  /** A registered form on the active step has unsaved typed or AI-filled values. */
  hasUnsavedEdits: boolean
  setHasUnsavedEdits: (sourceId: string, hasUnsavedEdits: boolean) => void
}

const StepValidationContext = createContext<StepValidationContextType>({
  validationResult: undefined,
  setValidationResult: () => {},
  navigationGuard: null,
  setNavigationGuard: () => {},
  hasUnsavedEdits: false,
  setHasUnsavedEdits: () => {},
})

export const StepValidationProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [validationResult, setValidationResultState] =
    useState<StepValidationResult>(undefined)
  const [navigationGuard, setNavigationGuardState] =
    useState<NavigationGuard | null>(null)

  const [unsavedEditSources, setUnsavedEditSources] = useState<
    ReadonlySet<string>
  >(() => new Set())

  const setHasUnsavedEdits = useCallback(
    (sourceId: string, hasUnsavedEdits: boolean) => {
      setUnsavedEditSources((prev) => {
        if (prev.has(sourceId) === hasUnsavedEdits) return prev
        const next = new Set(prev)
        if (hasUnsavedEdits) next.add(sourceId)
        else next.delete(sourceId)
        return next
      })
    },
    []
  )

  const hasUnsavedEdits = unsavedEditSources.size > 0

  const setNavigationGuard = useCallback((guard: NavigationGuard | null) => {
    setNavigationGuardState(() => guard)
  }, [])

  const value = useMemo<StepValidationContextType>(
    () => ({
      validationResult,
      setValidationResult: setValidationResultState,
      navigationGuard,
      setNavigationGuard,
      hasUnsavedEdits,
      setHasUnsavedEdits,
    }),
    [
      validationResult,
      navigationGuard,
      setNavigationGuard,
      hasUnsavedEdits,
      setHasUnsavedEdits,
    ]
  )

  return (
    <StepValidationContext.Provider value={value}>
      {children}
    </StepValidationContext.Provider>
  )
}

export const useStepValidation = () => useContext(StepValidationContext)

/**
 * Pipes a step's derived validation result into the framework. Single
 * top-level slot owned by the active step; `WorkflowStepContainer` clears it
 * on step transitions, so each step's report replaces the previous one.
 */
export const useReportValidation = (validation: StepValidationResult): void => {
  const { setValidationResult } = useStepValidation()

  const status = validation?.status
  const message = validation?.message
  const stable = useMemo<StepValidationResult>(
    () => (status ? { status, message: message ?? '' } : undefined),
    [status, message]
  )
  useEffect(() => {
    setValidationResult(stable)
  }, [setValidationResult, stable])
}

/**
 * Returns a guardNavigation function. Call it in place of any navigation
 * action. If a navigationGuard is registered it will be invoked with the
 * proceed callback; otherwise the action runs immediately.
 */
export const useNavigationGuard = () => {
  const { navigationGuard } = useStepValidation()

  const guardNavigation = useCallback(
    (proceed: () => void) => {
      if (navigationGuard) {
        navigationGuard(proceed)
      } else {
        proceed()
      }
    },
    [navigationGuard]
  )

  return { guardNavigation }
}
